import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Activity, Calendar, ChevronLeft, ChevronRight, Clock, FileText, MapPin, Star, ThermometerSun, User } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import CareOfficerProfileModal from '../../components/shared/CareOfficerProfileModal';
import EvidenceButton from '../../components/shared/EvidenceButton';
import { getEvidenceItemsForField } from '../../lib/evidenceUtils';
import {
  formatWatDateTime,
  getChecklistValue,
  getStatusClasses,
  getVisitActionLabel,
  getVisitAddress,
  getVisitDependantName,
  getVisitStart,
  normalizeVisitStatus,
  sortVisitsNewestFirst,
} from '../../lib/visitUtils';

const PAGE_SIZE = 8;

const statusOptions = [
  { value: 'all', label: 'All' },
  { value: 'Scheduled', label: 'Scheduled' },
  { value: 'Submitted', label: 'Awaiting Review' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Flagged', label: 'Flagged' },
  { value: 'Rejected', label: 'Rejected' },
];

const ScoreSnippet = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl border border-[#E8E6E3] bg-white p-3">
    <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {label}
    </p>
    <p className="mt-1 text-sm font-medium text-[#1A1A1A]">{value || '-'}</p>
  </div>
);

const SubscriberVisitCardReport = ({ visit }) => {
  const evidence = visit.evidence || {};
  const comments = getChecklistValue(visit, ['generalComments'], '') || getChecklistValue(visit, ['otherObservations'], '');
  return (
    <div className="border-t border-[#E8E6E3] bg-[#F7F4EE] p-4">
      <div className="grid gap-3 md:grid-cols-4">
        <ScoreSnippet icon={Activity} label="Wellness" value={visit.overallWellnessScore || '-'} />
        <ScoreSnippet icon={ThermometerSun} label="Temperature" value={`${getChecklistValue(visit, ['vitals', 'bodyTemperature', 'value']) || '-'} C`} />
        <ScoreSnippet label="Blood pressure" value={`${getChecklistValue(visit, ['vitals', 'bloodPressure', 'systolic']) || '-'}/${getChecklistValue(visit, ['vitals', 'bloodPressure', 'diastolic']) || '-'}`} />
        <ScoreSnippet label="Food" value={getChecklistValue(visit, ['sustenance', 'food', 'score']) || '-'} />
      </div>
      {comments && <p className="mt-3 rounded-2xl border border-[#E8E6E3] bg-white p-3 text-sm text-[#5A5A5A]">{comments}</p>}
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <EvidenceButton compact label="Physical condition" reference={evidence.physicalCondition} items={getEvidenceItemsForField(visit, 'physicalCondition')} />
        <EvidenceButton compact label="Food" reference={evidence.food} items={getEvidenceItemsForField(visit, 'food')} />
        <EvidenceButton compact label="Groceries" reference={evidence.groceries} items={getEvidenceItemsForField(visit, 'groceries')} />
        <EvidenceButton compact label="Drinking water" reference={evidence.drinkingWater} items={getEvidenceItemsForField(visit, 'drinkingWater')} />
      </div>
    </div>
  );
};

const VisitsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dependantFilter = searchParams.get('dependant') || 'all';
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [page, setPage] = useState(1);
  const [profileVisit, setProfileVisit] = useState(null);

  const {
    role,
    currentUser,
    visits,
    dependants,
    getSubscriberVisits,
    getDependantById,
    getCareOfficerById,
    getVisitRating,
    submitVisitRating,
    startCareOfficerConversation,
  } = useApp();

  const openCareOfficerChat = async ({ officerId, visit }) => {
    const conversation = await startCareOfficerConversation(officerId, visit?.id || null);
    setProfileVisit(null);
    navigate(`/messages?conversation=${conversation.id}`);
  };

  const baseVisits = useMemo(() => {
    if (role === 'care_officer') {
      const officerId = currentUser?.id || currentUser?.profileId;
      return visits.filter((visit) => !officerId || visit.careOfficerId === officerId);
    }
    if (role === 'subscriber') {
      return getSubscriberVisits(currentUser?.id);
    }
    return visits;
  }, [currentUser?.id, currentUser?.profileId, getSubscriberVisits, role, visits]);

  const dependantOptions = useMemo(() => {
    const ids = new Set(baseVisits.map((visit) => visit.dependantId).filter(Boolean));
    return Array.from(ids).map((id) => {
      const dependant = dependants.find((item) => item.id === id);
      const visit = baseVisits.find((item) => item.dependantId === id);
      return { id, name: getVisitDependantName(visit, dependant) };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [baseVisits, dependants]);

  const filteredVisits = useMemo(() => {
    return sortVisitsNewestFirst(baseVisits)
      .filter((visit) => dependantFilter === 'all' || visit.dependantId === dependantFilter)
      .filter((visit) => statusFilter === 'all' || normalizeVisitStatus(visit.status) === statusFilter)
      .map((visit) => ({
        ...visit,
        dependant: dependants.find((item) => item.id === visit.dependantId),
      }));
  }, [baseVisits, dependantFilter, dependants, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredVisits.length / PAGE_SIZE));
  const visibleVisits = filteredVisits.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const selectedDependant = dependantFilter !== 'all' ? getDependantById(dependantFilter) : null;

  const setDependantFilter = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('dependant');
    else next.set('dependant', value);
    setSearchParams(next);
    setPage(1);
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    setPage(1);
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('status');
    else next.set('status', value);
    setSearchParams(next);
  };

  const pageTitle = role === 'care_officer'
    ? 'My Visits'
    : role === 'dependant'
      ? 'My Visits'
      : role === 'supervisor' || role === 'admin'
        ? 'All Visits'
        : 'Visit Reports';

  return (
    <div className="min-h-screen bg-[#F7F4EE]" data-testid="visits-page">
      <div className="border-b border-[#E8E6E3] bg-[#FAFAF7]">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:py-8">
          <h1 className="font-serif text-2xl font-semibold text-[#1A1A1A] sm:text-3xl">{pageTitle}</h1>
          <p className="mt-1 text-sm text-[#5A5A5A]">
            {filteredVisits.length} visit{filteredVisits.length !== 1 ? 's' : ''}
            {selectedDependant && ` for ${selectedDependant.firstName || selectedDependant.name || 'this dependant'}`}
          </p>
        </div>
      </div>

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:py-8">
        <div className="mb-5 grid gap-3 rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-4 md:grid-cols-[1fr_1fr_auto]">
          {role !== 'dependant' && (
            <label className="text-sm font-medium text-[#1A1A1A]">
              Dependant
              <select
                value={dependantFilter}
                onChange={(event) => setDependantFilter(event.target.value)}
                className="mt-2 h-11 w-full rounded-full border border-[#D8D5D0] bg-[#FAFAF7] px-4 text-sm"
              >
                <option value="all">All dependants</option>
                {dependantOptions.map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
            </label>
          )}
          <label className="text-sm font-medium text-[#1A1A1A]">
            Status
            <select
              value={statusFilter}
              onChange={(event) => handleStatusFilter(event.target.value)}
              className="mt-2 h-11 w-full rounded-full border border-[#D8D5D0] bg-[#FAFAF7] px-4 text-sm"
            >
              {statusOptions.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => {
              setDependantFilter('all');
              handleStatusFilter('all');
            }}
            className="self-end rounded-full border border-[#9F1D35] px-5 py-3 text-sm font-medium text-[#9F1D35]"
          >
            Clear Filters
          </button>
        </div>

        <div className="space-y-4">
          {visibleVisits.map((visit) => {
            const isScheduled = normalizeVisitStatus(visit.status) === 'Scheduled';
            const isFlagged = normalizeVisitStatus(visit.status) === 'Flagged';
            const detailsRoute = role === 'care_officer' && isScheduled
              ? `/visits/${visit.id}/checklist`
              : role === 'care_officer' && isFlagged
                ? `/visits/${visit.id}/checklist?mode=correction`
                : `/visits/${visit.id}`;
            const comments = getChecklistValue(visit, ['generalComments'], '') || getChecklistValue(visit, ['otherObservations'], '');
            const careOfficer = getCareOfficerById(visit.careOfficerId || visit.care_officer_id);
            const careOfficerName = visit.careOfficerName || visit.careOfficerSnapshot?.name || `${careOfficer?.firstName || ''} ${careOfficer?.lastName || ''}`.trim() || 'Care officer';
            const isSubscriber = role === 'subscriber';
            const isDependant = role === 'dependant';
            const canRateVisit = (isSubscriber && !isScheduled) || (isDependant && normalizeVisitStatus(visit.status) === 'Approved');
            const rating = getVisitRating(visit.id);
            const ratingScore = rating?.subscriberRating?.score || rating?.subscriber_rating?.score || rating?.dependantRating?.score || rating?.dependant_rating?.score;

            return (
              <article
                key={visit.id}
                className="overflow-hidden rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7]"
                data-testid={`visit-card-${visit.id}`}
              >
                <div className="grid gap-4 p-4 sm:p-5 lg:grid-cols-[1fr_auto] lg:items-center">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-serif text-xl font-semibold text-[#1A1A1A]">{getVisitDependantName(visit, visit.dependant)}</h3>
                      <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${getStatusClasses(visit.status)}`}>
                        {normalizeVisitStatus(visit.status)}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2 text-sm text-[#5A5A5A]">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {formatWatDateTime(getVisitStart(visit))}
                      </span>
                      {visit.duration ? (
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {visit.duration} mins
                        </span>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => setProfileVisit(visit)}
                        className="flex items-center gap-1 rounded-full text-[#9F1D35] underline-offset-4 hover:underline"
                      >
                        <User className="h-4 w-4" />
                        {careOfficerName}
                      </button>
                      {(isSubscriber || isDependant) && ratingScore && (
                        <span className="flex items-center gap-1 text-[#9F1D35]">
                          <Star className="h-4 w-4 fill-[#C9A961] text-[#C9A961]" />
                          {ratingScore}
                        </span>
                      )}
                    </div>
                    <p className="mt-2 flex items-start gap-2 text-sm text-[#5A5A5A]">
                      <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0" />
                      {getVisitAddress(visit, visit.dependant)}
                    </p>
                    {comments && <p className="mt-3 line-clamp-2 text-sm text-[#5A5A5A]">{comments}</p>}
                  </div>

                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={() => navigate(detailsRoute)}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-[#9F1D35] px-5 py-3 text-sm font-medium text-[#FAFAF7] hover:bg-[#7A1628]"
                    >
                      <FileText className="h-4 w-4" />
                      {role === 'care_officer' ? getVisitActionLabel(visit) : (isSubscriber || isDependant) ? 'Expand Report' : 'View Visit'}
                    </button>
                    {canRateVisit && (
                      <button
                        type="button"
                        onClick={() => setProfileVisit(visit)}
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-[#9F1D35] px-5 py-3 text-sm font-medium text-[#9F1D35]"
                      >
                        <Star className="h-4 w-4" />
                        {rating ? 'Update Rating' : 'Rate Visit'}
                      </button>
                    )}
                  </div>
                </div>
                {(isSubscriber || isDependant) && !isScheduled && <SubscriberVisitCardReport visit={visit} />}
              </article>
            );
          })}

          {visibleVisits.length === 0 && (
            <div className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] py-16 text-center">
              <Calendar className="mx-auto mb-4 h-16 w-16 text-[#D8D5D0]" />
              <h3 className="font-serif text-xl font-semibold text-[#1A1A1A]">No visits found</h3>
              <p className="mt-1 text-[#5A5A5A]">Try clearing filters or checking another schedule window.</p>
            </div>
          )}
        </div>

        {filteredVisits.length > PAGE_SIZE && (
          <div className="mt-6 flex items-center justify-between">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
              className="inline-flex items-center gap-2 rounded-full border border-[#D8D5D0] px-4 py-2 text-sm disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>
            <span className="text-sm text-[#5A5A5A]">Page {page} of {totalPages}</span>
            <button
              type="button"
              disabled={page === totalPages}
              onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
              className="inline-flex items-center gap-2 rounded-full border border-[#D8D5D0] px-4 py-2 text-sm disabled:opacity-50"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </main>

      {profileVisit && (
        <CareOfficerProfileModal
          officer={getCareOfficerById(profileVisit.careOfficerId || profileVisit.care_officer_id)}
          visit={profileVisit}
          existingRating={getVisitRating(profileVisit.id)}
          canRate={role === 'subscriber' || (role === 'dependant' && normalizeVisitStatus(profileVisit.status) === 'Approved')}
          onSubmitRating={submitVisitRating}
          onStartChat={role === 'subscriber' || role === 'dependant' ? openCareOfficerChat : undefined}
          onClose={() => setProfileVisit(null)}
        />
      )}
    </div>
  );
};

export default VisitsPage;
